import type { PreflightReport, PreflightIssue } from '../types';

/**
 * Walk the live DOM and report contract violations the SDK would otherwise
 * silently tolerate at runtime. Intended for use inside the editor preview
 * (PT.getPreflightReport()) to surface "if this were live, X would break".
 */
export function runPreflight(): PreflightReport {
  const issues: PreflightIssue[] = [];

  // ── dynamic-form wrappers must carry data-form-name ──
  const formWrappers = Array.from(
    document.querySelectorAll<HTMLElement>('[data-pt-component="dynamic-form"]'),
  );
  const seenNames = new Map<string, number>();
  for (const w of formWrappers) {
    const name = w.getAttribute('data-form-name') || '';
    const ptId = w.getAttribute('data-pt-id') || '';
    if (!name) {
      issues.push({
        level: 'error',
        code: 'FORM_NO_NAME',
        message: 'dynamic-form is missing data-form-name; SDK cannot populate formName in payload',
        selector: ptId ? `[data-pt-id="${ptId}"]` : '[data-pt-component="dynamic-form"]',
      });
      continue;
    }
    seenNames.set(name, (seenNames.get(name) || 0) + 1);
    if (!w.querySelector('form')) {
      issues.push({
        level: 'error',
        code: 'FORM_NO_FORM_ELEMENT',
        message: 'dynamic-form wrapper "' + name + '" contains no <form>',
        selector: `[data-form-name="${name}"]`,
      });
    }
  }
  seenNames.forEach((count, name) => {
    if (count > 1) {
      issues.push({
        level: 'warn',
        code: 'FORM_DUPLICATE_NAME',
        message: 'data-form-name "' + name + '" appears ' + count + ' times; form: refs will hit the first match only',
        selector: `[data-form-name="${name}"]`,
      });
    }
  });

  // ── form: refs must point at an existing form (data-pt-href = current, href = legacy) ──
  const formRefs = Array.from(
    document.querySelectorAll<HTMLElement>('[data-pt-href^="form:"], [href^="form:"]'),
  );
  for (const ref of formRefs) {
    const attr =
      (ref.getAttribute('data-pt-href') || '').startsWith('form:') ? 'data-pt-href' : 'href';
    const href = ref.getAttribute(attr) || '';
    const target = href.slice(5);
    if (!target) {
      issues.push({
        level: 'error',
        code: 'FORM_REF_EMPTY',
        message: attr + '="form:" is missing a target name',
        selector: attrSelector(attr, href),
      });
      continue;
    }
    const found =
      document.querySelector(`[data-form-name="${cssEscape(target)}"]`) ||
      document.querySelector(`[data-pt-id="${cssEscape(target)}"]`);
    if (!found) {
      issues.push({
        level: 'error',
        code: 'FORM_REF_UNRESOLVED',
        message: attr + '="form:' + target + '" does not match any data-form-name or data-pt-id',
        selector: attrSelector(attr, href),
      });
    }
  }

  // ── data-pt-track JSON must parse ──
  const trackEls = Array.from(document.querySelectorAll<HTMLElement>('[data-pt-track]'));
  for (const el of trackEls) {
    const raw = el.getAttribute('data-pt-track') || '';
    if (!raw) {
      issues.push({
        level: 'warn',
        code: 'TRACK_EMPTY',
        message: 'data-pt-track is empty',
      });
      continue;
    }
    if (raw[0] === '{' || raw[0] === '[') {
      try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || !parsed.eventName) {
          issues.push({
            level: 'error',
            code: 'TRACK_NO_EVENT_NAME',
            message: 'data-pt-track JSON is missing eventName',
          });
        }
      } catch (e) {
        issues.push({
          level: 'error',
          code: 'TRACK_INVALID_JSON',
          message: 'data-pt-track JSON failed to parse: ' + (e as Error).message,
        });
      }
    }
  }

  // ── click-action elements with empty/invalid action types ──
  // [data-pt-href] = current contract (any tag); tag-scoped [href] = legacy pages.
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-pt-href], button[href], img[href], p[href], div[href], span[href], h1[href], h2[href], h3[href], h4[href], h5[href], h6[href], [data-pt-action-enabled="true"]',
    ),
  );
  for (const el of candidates) {
    if (el.tagName === 'A' && !el.hasAttribute('data-pt-href')) continue;
    const href = el.getAttribute('data-pt-href') ?? el.getAttribute('href') ?? '';
    if (!href) {
      issues.push({
        level: 'warn',
        code: 'CTA_NO_HREF',
        message: 'CTA element has data-pt-action-enabled but no click action',
      });
      continue;
    }
    if (href === 'javascript:void(0)' && !el.hasAttribute('data-pt-onclick')) {
      issues.push({
        level: 'warn',
        code: 'CTA_JS_EMPTY',
        message: 'CTA uses javascript:void(0) but has no data-pt-onclick',
      });
    }
  }

  // ── postal-autofill group / role consistency ──
  const postalCodes = Array.from(
    document.querySelectorAll<HTMLElement>('[data-postal-role="code"]'),
  );
  for (const code of postalCodes) {
    const group = code.getAttribute('data-postal-group');
    if (!group) {
      issues.push({
        level: 'error',
        code: 'POSTAL_NO_GROUP',
        message: 'postal "code" input has no data-postal-group',
      });
      continue;
    }
    const peers = document.querySelectorAll(`[data-postal-group="${cssEscape(group)}"]`);
    if (peers.length < 2) {
      issues.push({
        level: 'warn',
        code: 'POSTAL_LONE_CODE',
        message: 'postal-group "' + group + '" has only the "code" field; nothing to auto-fill',
      });
    }
  }

  const errCount = issues.filter((i) => i.level === 'error').length;
  return { ok: errCount === 0, issues };
}

function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

function attrSelector(attr: string, value: string): string {
  return `[${attr}="${cssEscape(value)}"]`;
}
